import { LanguageContext } from "../../../../context/LanguageContext";
import { AmountBox, AppAmountBoxWrapper } from "../../../common";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  ITEMS_PER_PAGE,
  TABLE_FILTERS_WITHOUT_CATEGORY,
} from "../../../../constants";
import { AppPagination } from "../../../../Libraries";
import { useAllInvoiceReport } from "../../../../hooks/useInvoiceList";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { FilterNonIdeal } from "../../../common/NewNonIdeal";
import { useLocation, useNavigate } from "react-router-dom";
import InvoiceRowItem from "./InvoiceRowItem";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import AppTableFilters from "../../../common/AppTableFilters";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import { buildFiltersConfig, dateFormatInNumberWithSpace } from "../../../../utils";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import { useWalletContext } from "../../../../context/WalletContext";
import SalePurchaseListTableHeader from "./SalePurchaseListTableHeader";
import withObservables from "@nozbe/with-observables";
import { of } from "rxjs";

export { InvListTableHeads } from "./SalePurchaseListTableHeader";

const InvoicePageContent = {
  title: {
    in: "Sales invoice",
    out: "Purchase order",
  },
  create_btn: {
    in: "Create sales invoice",
    out: "Create purchase order",
  },
  invoice_info2: {
    in: "Outstanding",
    out: "Outstanding",
  },
  invoice_info3: {
    in: "Overdue",
    out: "Overdue",
  },
  invoice_info1: {
    in: "Paid",
    out: "Paid",
  },
  defaultPrefix: {
    in: "INV#",
    out: "PO#",
  },
};

const SalePurchaseList = ({
  type = "in",
  business,
  user,
  accountWrapperClassName,
  accountListBodyWarpperClassName,
  customers,
  initialInvoices,
}) => {
  const [page, setPage] = useState(0);
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
  } = useFiltersHandler(undefined, I18n);

  const [selectedContactId, setSelectedContactId] = useState("");
  const justClosedDropdownRef = useRef(false);

  const { onDownload, downloadLoading } = useDownlaodPdf(user);

  const { accountInfo, fetchAccountInfo, isAccountLoading } =
    useWalletContext();

  const {
    totalInvoiceFilterCount,
    invoiceList,
    totalPaid,
    totalUnPaid,
    totalOverdue,
    handleInvoice,
    isLoading,
  } = useAllInvoiceReport({
    business,
    type,
    filters,
    initialInvoices,
  });

  const initialGetData = () => {
    setPage(0);
    handleInvoice(ITEMS_PER_PAGE, 0);
  };

  useEffect(() => {
    initialGetData();
  }, [filters]);

  const InvoiceData = useMemo(() => {
    let MappedInvList = invoiceList?.map((invoiceDt) => {
      const isPaid = invoiceDt?.status === "paid";
      const isOverdue =
        !isPaid &&
        invoiceDt?.due_date &&
        invoiceDt?.due_date > 0 &&
        new Date(invoiceDt?.due_date) < new Date();

      return {
        id: invoiceDt?.id,
        date: dateFormatInNumberWithSpace(invoiceDt?.transacted_at),
        inv_num: `${
          invoiceDt?.invoice_prefix || InvoicePageContent.defaultPrefix[type]
        }${invoiceDt?.invoice_number}`,
        amount: Number(invoiceDt?.amount).toFixed(2),
        received: invoiceDt?.advance_amount,
        balance: invoiceDt?.status?.toLowerCase() == "paid" ? 0 :  Number(
          Number(invoiceDt?.amount) - Number(invoiceDt?.advance_amount),
        ).toFixed(2),
        profit: invoiceDt?.profit || 0,
        status: isOverdue ? "overdue" : invoiceDt?.status,
        method: invoiceDt?.method || "",
        invoice_number: invoiceDt?.invoice_number,
        invoice_prefix: invoiceDt?.invoice_prefix,
        due_date:
          invoiceDt?.due_date && invoiceDt?.due_date > 0
            ? dateFormatInNumberWithSpace(invoiceDt?.due_date)
            : "-",
      };
    });
    return MappedInvList;
  }, [invoiceList, filters]);

  const invoiceStatusList = [
    {
      amount: formatNum(totalUnPaid),
      title: I18n.outstanding,
      id: InvoicePageContent.invoice_info2[type],
      rsLabel: true,
    },

    {
      amount: formatNum(totalOverdue),
      title: I18n.overdue,
      id: InvoicePageContent.invoice_info3[type],
      rsLabel: true,
    },

    {
      amount: formatNum(totalPaid),
      title: I18n.paid,
      id: InvoicePageContent.invoice_info1[type],
      rsLabel: true,
    },
  ];

  const handleDownloadReport = (startDate, endDate) => {
    triggerLogEvent(analytics, `${type == "in" ? "sale_invoice" : "purchase_order"}_report_download`);
    let paramsForReport = {
      name: "invoice_statement.pdf",
      template_name: "accountreport_new.hbs",
      business_id: business.id,
      start_date: startDate || "",
      end_date: endDate || "",
      type: "invoice",
      invoice_type: type === "in" ? "sales" : "expense",
      pdf: true,
    };

    onDownload(paramsForReport, () => {}, navigate);
  };

  let manual_status = useMemo(() => {
    return [
      { label: I18n.paid, value: "paid" },
      { label: I18n.unpaid, value: "unpaid" },
      { label: I18n.overdue, value: "overdue" },
    ];
  }, [I18n]);

  return (
    <Box className={accountWrapperClassName || "AccountWrapper"}>
      <AppTableFilters
        filters={TABLE_FILTERS_WITHOUT_CATEGORY}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => handleRemoveChip(chip)}
        filtersConfig={buildFiltersConfig(TABLE_FILTERS_WITHOUT_CATEGORY, [], manual_status, null, I18n.search_by_contact_or_invoice_no || "Search by contact or invoice no", null, null, null, null, I18n)}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
        exportConfig={{
          onExport: handleDownloadReport,
          loading: downloadLoading,
          dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
          applyBtnText: I18n.export,
          spinnerColor: "mainColor",
        }}
      />

      <AppAmountBoxWrapper>
        <AmountBox
          isNonIdeal={totalInvoiceFilterCount <= 0}
          data={invoiceStatusList}
        />
      </AppAmountBoxWrapper>

      <SalePurchaseListTableHeader
        filters={filters}
        handleFilterChange={handleFilterChange}
        type={type}
        disabled={!InvoiceData?.length}
      />

      <Box
        className={`${accountListBodyWarpperClassName || "accountListBodyWarpper"} generic-scrollbar`}
        id="scrollableDiv"
      >
        {totalInvoiceFilterCount <= 0 && !isLoading ? (
          <Box className="h-100">
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={I18n.try_adjusting_filters}
              subTitleTextOnClick={() => resetFilters()}
              subTitleText={I18n.reset_small}
            />
          </Box>
        ) : (
          <AppPagination
            data={{
              handleNext: (e) => {
                if (InvoiceData.length < totalInvoiceFilterCount) {
                  setPage((prev) => {
                    handleInvoice(
                      ITEMS_PER_PAGE,
                      (prev + 1) * ITEMS_PER_PAGE,
                      false,
                    );
                    return prev + 1;
                  });
                }
              },
              pageLoad: true,
              count: totalInvoiceFilterCount,
              list: InvoiceData,
              scrollableTarget: "scrollableDiv",
            }}
          >
            {InvoiceData?.map((invoiceDt, ind) => {
              return (
                <InvoiceRowItem
                  key={invoiceDt.id}
                  invoice={invoiceList[ind]}
                  invoiceDetail={invoiceDt}
                  refetchInvoiceList={initialGetData}
                  navigate={navigate}
                  location={location}
                  type={type}
                  index={ind}
                  accountInfo={accountInfo}
                  fetchAccountInfo={fetchAccountInfo}
                  isAccountLoading={isAccountLoading}
                  business={business}
                  selectedContactId={selectedContactId}
                  setSelectedContactId={setSelectedContactId}
                  justClosedDropdownRef={justClosedDropdownRef}
                  customers={customers}
                />
              );
            })}
          </AppPagination>
        )}
      </Box>
    </Box>
  );
};

const enhance = withObservables(
  ["business", "type", "filters"],
  ({ business, type, filters }) => ({
    customers: business ? business?.getAllCustomers.observe() : of([]),
    initialInvoices: business
      ? business
          .getInvoiceReport(
            filters?.date?.startDate,
            filters?.date?.endDate,
            filters?.keywords || "",
            type,
            filters?.status,
            undefined,
            undefined,
            undefined,
            filters?.amount,
            filters?.method,
          )
          .observe()
      : of([]),
  }),
);

export default enhance(SalePurchaseList);
